/**
 * PD-17094 — Projects API shapes (sales relationships plan, section 4). A
 * project is the construction project several customers may bid on; they
 * take part through their opportunities and jobs.
 */
import type { ProjectEntity, ProjectStatus } from '../entity/project.entity';

export const PROJECT_STATUS_LABELS: Readonly<Record<ProjectStatus, string>> = {
	bidding: 'Bidding',
	awarded: 'Awarded',
	in_progress: 'In Progress',
	complete: 'Complete',
	canceled: 'Canceled',
};

/** The fields a rep edits on a project. */
export type ProjectEditableFieldsDto = Pick<
	ProjectEntity,
	| 'name'
	| 'status'
	| 'description'
	| 'address1'
	| 'city'
	| 'state'
	| 'zip'
	| 'bidDate'
	| 'fkClientIdGeneralContractor'
	| 'generalContractorName'
	| 'fkClientIdOwner'
	| 'ownerName'
>;

/** One row of the Projects list. */
export interface ProjectListRowDto extends ProjectEditableFieldsDto, Pick<ProjectEntity, 'id'> {
	/** The general contractor's customer name, else the typed name. */
	generalContractorLabel: string | null;
	/** The building owner's customer name, else the typed name. */
	ownerLabel: string | null;
	/** Distinct customers with an opportunity or a job on the project. */
	customerCount: number;
	opportunityCount: number;
	jobCount: number;
	createdDate: string;
}

/**
 * A customer's part in a project: an opportunity (the bid) or a job (won
 * work). Customers the caller may not open show their name and where they
 * stand, but no amounts and no link (customer visibility, JQ-3).
 */
export interface ProjectParticipantDto {
	kind: 'opportunity' | 'job';
	id: number;
	name: string;
	clientId: number;
	clientName: string;
	canOpen: boolean;
	/** The opportunity's stage name, or the job's status label. */
	stageLabel: string;
	/** The opportunity amount or the job's estimated value; null when not open to the caller. */
	amount: number | null;
	/** A job's ordered to date; null for an opportunity or when not open to the caller. */
	orderedTotal: number | null;
}

export interface ProjectDto extends ProjectListRowDto {
	participants: Array<ProjectParticipantDto>;
}

/** POST /api/projects */
export type CreateProjectRequestDto = Pick<ProjectEditableFieldsDto, 'name'> &
	Partial<Omit<ProjectEditableFieldsDto, 'name'>>;

/** PUT /api/projects/:id — only the fields sent change. */
export type UpdateProjectRequestDto = Partial<ProjectEditableFieldsDto>;

/** A project in a picker. */
export interface ProjectOptionDto {
	id: number;
	name: string;
	status: ProjectStatus;
	city: string | null;
	state: string | null;
}
